import * as React from "react"

type Theme = "light" | "dark" | "system"

interface ThemeState {
  theme: Theme
  resolvedTheme: "light" | "dark"
  setTheme: (theme: Theme) => void
}

const STORAGE_KEY = "ai-agents.theme"
const ThemeContext = React.createContext<ThemeState | null>(null)

function systemTheme(): "light" | "dark" {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as Theme) || "system"
    } catch {
      return "system"
    }
  })
  const [resolved, setResolved] = React.useState<"light" | "dark">(() =>
    theme === "system" ? systemTheme() : theme,
  )

  React.useEffect(() => {
    const next = theme === "system" ? systemTheme() : theme
    setResolved(next)
    document.documentElement.classList.toggle("dark", next === "dark")

    if (theme !== "system") return
    // Follow the OS while no explicit choice has been made.
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const listener = () => {
      const updated = systemTheme()
      setResolved(updated)
      document.documentElement.classList.toggle("dark", updated === "dark")
    }
    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [theme])

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage unavailable; the choice simply will not persist */
    }
  }, [])

  const value = React.useMemo(
    () => ({ theme, resolvedTheme: resolved, setTheme }),
    [theme, resolved, setTheme],
  )
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeState {
  const context = React.useContext(ThemeContext)
  if (!context) throw new Error("useTheme must be used inside ThemeProvider")
  return context
}
